import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';

export function Alerts() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => api.getAlerts({ acknowledged: 'false' }),
  });

  const acknowledgeAlert = useMutation({
    mutationFn: (id: string) => api.acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const alerts = data?.alerts || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Alerts</h2>
          <p className="text-sm text-muted-foreground">
            Monitor and acknowledge system alerts
          </p>
        </div>
        <div className="flex gap-2">
          <select className="h-9 rounded-md border px-3 text-sm">
            <option value="">All Types</option>
            <option value="LIMIT_WARNING">Limit Warning</option>
            <option value="LIMIT_BREACH">Limit Breach</option>
            <option value="IMPORT_ERROR">Import Error</option>
            <option value="SYSTEM">System</option>
          </select>
          <select className="h-9 rounded-md border px-3 text-sm">
            <option value="false">Unacknowledged</option>
            <option value="true">Acknowledged</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
            Loading alerts...
          </div>
        ) : alerts.length === 0 ? (
          <div className="rounded-lg border bg-card p-8 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No unacknowledged alerts</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div
              key={alert.id}
              className={cn(
                'rounded-lg border bg-card p-4',
                alert.severity === 'CRITICAL' && 'border-red-200 bg-red-50',
                alert.severity === 'WARNING' && 'border-yellow-200 bg-yellow-50',
                alert.severity === 'INFO' && 'border-blue-200 bg-blue-50'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'h-8 w-8 rounded-full flex items-center justify-center',
                    alert.severity === 'CRITICAL' && 'bg-red-100',
                    alert.severity === 'WARNING' && 'bg-yellow-100',
                    alert.severity === 'INFO' && 'bg-blue-100'
                  )}>
                    <Bell className={cn(
                      'h-4 w-4',
                      alert.severity === 'CRITICAL' && 'text-red-600',
                      alert.severity === 'WARNING' && 'text-yellow-600',
                      alert.severity === 'INFO' && 'text-blue-600'
                    )} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{alert.title}</h3>
                      <span className={cn(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        alert.severity === 'CRITICAL' && 'bg-red-100 text-red-700',
                        alert.severity === 'WARNING' && 'bg-yellow-100 text-yellow-700',
                        alert.severity === 'INFO' && 'bg-blue-100 text-blue-700'
                      )}>
                        {alert.severity}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-muted text-xs">
                        {alert.type.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDateTime(alert.createdAt)}
                    </p>
                  </div>
                </div>
                {!alert.isAcknowledged && (
                  <button
                    onClick={() => acknowledgeAlert.mutate(alert.id)}
                    disabled={acknowledgeAlert.isPending}
                    className="flex items-center gap-1 h-8 px-3 rounded-md border bg-background text-sm font-medium hover:bg-muted disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" />
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
